import {
  Component,
  OnInit,
  Input,
  forwardRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  FormGroup,
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormArray,
  ValidationErrors,
} from "@angular/forms";
import { Messages } from "../../model/messages";
import { noop, Observer, Subject } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject } from "rxjs";

export const CUSTOM_MESSAGES_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MessagesViewComponent),
  multi: true,
};

@Component({
  selector: "messages",
  templateUrl: "./messages.component.html",
  styleUrls: ["./messages.component.css"],
  providers: [CUSTOM_MESSAGES_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class MessagesViewComponent implements OnInit, ControlValueAccessor {
  @Input() form: FormGroup;
  @Input() public innerMessages: Messages;
  @Input() remap: string;
  @Input() public messagesObserver: Subject<Messages>;
  @Input() public fieldname: string;
  @Input() showFieldName: boolean = false;
  @Input() showNonFieldKeys: boolean = false;
  @Input() showFieldKeys: boolean = true;
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;
  private remapped: Map<string, string>;
  private _currentMessages: BehaviorSubject<Messages>;

  constructor(private translate: TranslateService) {
    this._currentMessages = new BehaviorSubject<Messages>(
      new Messages(new Map<string, string[]>())
    );
    this._currentMessages.subscribe((messages: Messages) => { });
  }

  ngOnInit() {
    if (this.messagesObserver) {
      this.messagesObserver.subscribe((messages) => {
        this._currentMessages.next(messages);
      });
    }
    this.initializeFieldNameRemapper();
    this.updateMessages();
  }

  writeValue(value: any) {
    if (value !== this.innerMessages && Messages.isAMessages(value)) {
      this.updateMessages();
    } else if (value === this.innerMessages) {
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = () => {
      fn();
    };
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = () => {
      fn();
    };
  }

  private updateMessages() {
    if (this.innerMessages) {
      this._currentMessages.next(this.innerMessages);
    }
  }

  getControlNames(): string[] {
    if (this.fieldname && this.fieldname.trim().length > 0) {
      return [this.fieldname.trim()];
    } else {
      return Object.keys(this.form.controls);
    }
  }

  getNonFieldKeys(): string[] {
    const controlsRemapped = [];
    for (let controlName of this.getControlNames()) {
      controlsRemapped.push(this.remapFieldName(controlName));
    }
    const otherKeys: string[] = [];
    for (let key of this._currentMessages.value.keysWithMessages) {
      if (controlsRemapped.findIndex((c) => key == c) < 0) {
        otherKeys.push(key);
      }
    }

    return otherKeys;
  }

  getErrors(controlName: string): string[] {
    const control = this.form.controls[controlName];
    if (control && control.errors) {
      return Object.keys(control.errors).map((errorKey) =>
        this.getErrorMessage(control, errorKey)
      );
    }
    return [];
  }

  private getErrorMessage(control: AbstractControl, errorKey: string): string {
    const errors = control.errors;
    if (errors) {
      const errorMessage = this.translate.instant(`errors.${errorKey}`);
      return errorMessage || errorKey;
    }
    return errorKey;
  }

  private initializeFieldNameRemapper(): void {
    this.remapped = new Map();
    if (this.remap) {
      const lines = this.remap.split(",");
      lines.forEach((line) => {
        const parts = line.split("=");
        if (parts.length > 1) {
          this.remapped.set(parts[0].trim(), parts[1].trim());
        }
      });
    }
  }

  remapFieldName(key: string): string {
    return this.remapped.get(key) || key;
  }

  getFieldErrors(fieldName: string): string[] {
    const control = this.form.get(fieldName);
    if (control && control.errors && (control.touched || control.dirty)) {
      return Object.keys(control.errors).map((errorKey) =>
        this.getErrorMessage(control, errorKey)
      );
    }
    return [];
  }

  getMessagesForField(fieldName: string): string[] {
    if (!this._currentMessages.value) return [];

    const remapped = this.remapFieldName(fieldName);
    const messagesForField = this._currentMessages.value.messagesFor(remapped);

    if (!messagesForField) return [];

    return messagesForField.map((msg) => {

      let translated = this.translate.instant(msg);


      if (!translated || translated === msg) {
        translated = this.translate.instant(`errors.${msg}`);
      }

      return translated && translated !== `errors.${msg}` ? translated : msg;
    });
  }


  private writeMessagesAsValidationErrorsToFields(messages: Messages): void {
    if (!messages) return;

    messages.keysWithMessages.forEach((key: string) => {
      const fields = this.remapFieldName(key);

      if (typeof fields === "string") {
        fields.split("+").forEach((fieldName: string) => {
          const control = this.form.get(fieldName);
          if (control) {
            this.writeMessagesAsValidationErrorToControl(
              messages,
              key,
              control
            );
          }
        });
      }
    });
  }

  private writeMessagesAsValidationErrorToControl(
    messages: Messages,
    key: string,
    control: AbstractControl
  ) {
    let addingErrors = control.errors || {};
    messages.messagesFor(key).forEach((message: string) => {
      addingErrors[message] = true;
    });
    control.setErrors(addingErrors);
  }
}
